import React, { useState, useEffect } from "react";
import { Button, Col, Container, Row, Form, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import FloatingButtons from "./FloatingButtons";
import { useAuth } from "../../contexts/AuthContext";

import {
  getAuth,
  signInWithEmailAndPassword,
  onAuthStateChanged,
} from "firebase/auth";

export default function AccountDeletion() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const Navigate = useNavigate();
  const auth = getAuth();
  const { logout } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);

    // Check if user is logged in
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setIsLoggedIn(!!currentUser);
      setUser(currentUser);
      if (currentUser) {
        setEmail(currentUser.email || "");
      }
    });

    return () => unsubscribe();
  }, [auth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Login successful, user state will update via onAuthStateChanged
    } catch (err) {
      setError("Invalid email or password. Please try again.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = () => {
    setShowConfirmModal(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
      console.log("Logged Out");
      Navigate("/");
    } catch {
      console.log("Failed to log out");
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Call your API endpoint
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/delete-account`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await user.getIdToken()}`,
          },
          body: JSON.stringify({ userId: user.uid }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setDeleteSuccess(true);
        setShowConfirmModal(false);
        // User will be automatically signed out by Firebase
        // after their account is deleted
        setTimeout(() => {
          handleLogout();
        }, 3000);
      } else {
        setError("Error: " + (result.error || "Failed to delete account"));
        setShowConfirmModal(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setError("An unexpected error occurred. Please try again later.");
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FloatingButtons />
      <section id="account-deletion" className="cs-bg-dark py-4 cs-section">
        <Container>
          <Row>
            <Col>
              <h1 className="fw-bold text-white">Account Deletion</h1>
            </Col>
          </Row>

          {deleteSuccess ? (
            <Row className="mt-4">
              <Col xs={12}>
                <div className="bg-success p-4 rounded">
                  <h3 className="text-white">Account Deleted Successfully</h3>
                  <p className="text-white">
                    Your account and all associated data have been permanently
                    deleted. Thank you for using our service.
                  </p>
                  <p className="text-white">
                    You will be redirected to the homepage in a few seconds...
                  </p>
                </div>
              </Col>
            </Row>
          ) : (
            <>
              <Row className="mt-3">
                <Col xs={12}>
                  <div className="bg-dark p-4 rounded border border-secondary">
                    <h3 className="text-white">Account Deletion Policy</h3>
                    <p className="text-white">When you delete your account:</p>
                    <ul className="text-white">
                      <li>
                        All your personal information will be permanently
                        removed from our systems
                      </li>
                      <li>
                        Your user profile, preferences, and authentication data
                        will be deleted
                      </li>
                      <li>
                        Any financial records will be anonymized and retained
                        for legal compliance
                      </li>
                      <li>This action cannot be undone</li>
                    </ul>
                    <p className="text-white">
                      Please note that deletion is permanent and cannot be
                      reversed.
                    </p>
                  </div>
                </Col>
              </Row>

              <Row className="mt-4 mobile-direction-column">
                <Col xs={12} md={8}>
                  {!isLoggedIn ? (
                    <div className="bg-dark text-white p-4 rounded">
                      <h3>Please Log In To Delete Your Account</h3>
                      <p>
                        For security reasons, you must log in before you can
                        delete your account.
                      </p>

                      <Form onSubmit={handleLogin}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Password</Form.Label>
                          <Form.Control
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                        </Form.Group>

                        {error && (
                          <div className="alert alert-danger">{error}</div>
                        )}

                        <Button
                          variant="primary"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Processing..." : "Log In"}
                        </Button>

                        <div className="mt-3">
                          <Link to="/auth">Forgot your password?</Link>
                        </div>
                      </Form>
                    </div>
                  ) : (
                    <div className="bg-light p-4 rounded">
                      <h3>Delete Your Account</h3>
                      <p>
                        You are logged in as: <strong>{email}</strong>
                      </p>
                      <p>
                        Warning: This action is permanent and cannot be undone.
                        All your personal data will be deleted.
                      </p>

                      {error && (
                        <div className="alert alert-danger">{error}</div>
                      )}

                      <Button
                        variant="danger"
                        onClick={handleDeleteConfirm}
                        disabled={loading}
                        className="mt-3"
                      >
                        {loading ? "Processing..." : "Delete My Account"}
                      </Button>
                    </div>
                  )}
                </Col>

                <Col
                  xs={12}
                  md={4}
                  className="text-white padding-mobile-0 px-4"
                >
                  <Row>
                    <Col className="d-flex flex-column">
                      <h2>Need Help?</h2>
                      <p>
                        If you're having trouble deleting your account or have
                        any questions, please contact our support team.
                      </p>
                      <Link
                        to={"tel:9362767787"}
                        className="contactLink text-white"
                      >
                        936-276-7787
                      </Link>
                      <Link
                        to={"mailto:info@BBRBassBoatRentals.com"}
                        className="contactLink text-white"
                      >
                        info@BBRBassBoatRentals.com
                      </Link>
                      <Link
                        to={"/contact"}
                        className="contactLink text-white mt-3"
                      >
                        Contact Us Form
                      </Link>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </section>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Account Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you absolutely sure you want to delete your account? This action
            cannot be undone.
          </p>
          <p>
            All your personal information and user data will be permanently
            removed from our systems.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteAccount}>
            Yes, Delete My Account
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
